import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { HpButton, HpCardHeader, FilterSelect, TableScroll } from '@hp/ui';
import { invalidateAll } from '../../api/queryClient';
import { trpc } from '../../api/trpc';
import { ErrorAlert } from '../../components/error-alert';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { fmtInt } from '../../lib/format';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
/** Plans can be kept per paid platform; the report prefers them when a channel filter is active. */
const PLAN_CHANNELS = ['', 'meta', 'google', 'sklik'] as const;
type PlanChannel = (typeof PLAN_CHANNELS)[number];

/** The approved monthly media budget (total per month) the dashboard's "spend and forecast" card compares against. */
export function BudgetEditor({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation(['performance', 'common']);
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [channel, setChannel] = useState<PlanChannel>('');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const channelOrNull = channel === '' ? null : channel;
  const plan = useQuery(trpc.performance.budget.list.queryOptions({ year }, { staleTime: 30_000 }));
  const invalidate = () => invalidateAll(trpc.performance.budget.pathFilter(), trpc.performance.report.pathFilter());
  const set = useMutation(trpc.performance.budget.set.mutationOptions({ onSuccess: invalidate }));
  const remove = useMutation(trpc.performance.budget.remove.mutationOptions({ onSuccess: invalidate }));
  const totals = new Map((plan.data ?? []).filter((r) => r.channel === channelOrNull).map((r) => [r.month, r]));
  const years = [thisYear - 2, thisYear - 1, thisYear, thisYear + 1];
  const sum = [...totals.values()].reduce((s, r) => s + r.amountCzk, 0);

  return (
    <section className="hp-card">
      <HpCardHeader title={t('budget.title')} subtitle={t('budget.subtitle')} icon="wallet">
        <FilterSelect
          value={channel}
          aria-label={t('budget.channelAria')}
          onChange={(e) => {
            setChannel(e.target.value as PlanChannel);
            setDrafts({});
          }}
        >
          {PLAN_CHANNELS.map((c) => (
            <option key={c} value={c}>
              {c === '' ? t('budget.totalPlan') : t(`channel.${c}`)}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect
          value={String(year)}
          aria-label={t('budget.yearAria')}
          onChange={(e) => setYear(Number(e.target.value))}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </FilterSelect>
      </HpCardHeader>
      <ErrorAlert error={set.error ?? remove.error ?? plan.error} className="bo-alert--inset" />
      <TableScroll label={t('budget.title')}>
        <BrutTable>
          <BrutTheadRow>
            <th>{t('budget.month')}</th>
            <th className="bo-num">{t('budget.amount')}</th>
            {canManage && <th>{t('budget.actions')}</th>}
          </BrutTheadRow>
          <tbody>
            {MONTHS.map((m) => {
              const month = `${year}-${String(m).padStart(2, '0')}-01`;
              const row = totals.get(month);
              const draft = drafts[month] ?? (row ? String(row.amountCzk) : '');
              const dirty = drafts[month] !== undefined && drafts[month] !== (row ? String(row.amountCzk) : '');
              return (
                <tr key={month}>
                  <td className="bo-mono">{month.slice(0, 7)}</td>
                  <td className="bo-num">
                    {canManage ? (
                      <input
                        type="number"
                        min={0}
                        step={1000}
                        value={draft}
                        placeholder="—"
                        aria-label={`${t('budget.amount')} ${month.slice(0, 7)}`}
                        onChange={(e) => setDrafts((d) => ({ ...d, [month]: e.target.value }))}
                      />
                    ) : row ? (
                      fmtInt(row.amountCzk)
                    ) : (
                      '—'
                    )}
                  </td>
                  {canManage && (
                    <td>
                      <span className="bo-row">
                        <HpButton
                          variant="secondary"
                          icon="check"
                          className="bo-button--sm"
                          busy={set.isPending && set.variables?.month === month}
                          disabled={!dirty || draft === '' || set.isPending}
                          onClick={() =>
                            set.mutate(
                              { month, channel: channelOrNull, amountCzk: Number(draft) },
                              { onSuccess: () => setDrafts((d) => ({ ...d, [month]: undefined as never })) },
                            )
                          }
                        >
                          {t('common:actions.save')}
                        </HpButton>
                        {row && (
                          <HpButton
                            variant="quiet"
                            icon="trash"
                            className="bo-button--sm"
                            busy={remove.isPending && remove.variables?.month === month}
                            disabled={remove.isPending}
                            onClick={() => remove.mutate({ month, channel: channelOrNull })}
                          >
                            {t('common:actions.remove')}
                          </HpButton>
                        )}
                      </span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td>{t('budget.total')}</td>
              <td className="bo-num">
                <strong>{fmtInt(Math.round(sum))}</strong>
              </td>
              {canManage && <td />}
            </tr>
          </tfoot>
        </BrutTable>
      </TableScroll>
      <p className="hp-card__body bo-hint">{t('budget.hint')}</p>
    </section>
  );
}
